import * as React from "react";

import { cn } from "@utils/ui";

type InputProps = React.ComponentProps<"input"> & {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, rightIcon, ...props }, ref) => {
    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon;

    if (hasLeftIcon || hasRightIcon) {
      return (
        <div className="relative w-full">
          {leftIcon && (
            <figure className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              {leftIcon}
            </figure>
          )}
          <InputComponent
            type={type}
            className={cn(
              "flex-1",
              hasLeftIcon && "pl-10",
              hasRightIcon && "pr-10",
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <figure className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              {rightIcon}
            </figure>
          )}
        </div>
      );
    }

    return <InputComponent ref={ref} type={type} className={className} {...props} />;
  }
);

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ type, className, ...props }, ref) => (
  <input
    type={type}
    data-slot="input"
    className={cn(
      "placeholder:text-muted-foreground",
      "shadow-xs flex min-h-11 w-full min-w-0 rounded-lg border border-border bg-transparent p-3 text-base leading-relaxed outline-none transition-[color,box-shadow] md:text-sm",
      "selection:bg-primary selection:text-primary-foreground",
      "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
      "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
      "aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20",
      className
    )}
    ref={ref}
    {...props}
  />
));
InputComponent.displayName = "InputComponent";

Input.displayName = "Input";

export { Input };
